import { apiClient } from '@lib/axios';

export type MasterResource = 'banks' | 'courses' | 'centres' | 'main-centres';

export interface MasterItem {
  _id: string;
  name: string;
  code?: string;
  active: boolean;
}

/** A centre always belongs to a main centre. */
export interface CentreItem extends MasterItem {
  mainCentreId: string;
}

/**
 * The whole receipt identity lives on the main centre, shared by all its
 * centres: one GSTIN means one registered entity, so they bill under the same
 * name, address and contact. `name` is the city; `instituteName` is the legal
 * entity printed on receipts.
 */
export interface MainCentreItem extends MasterItem {
  instituteName?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  phoneNumber?: string;
  email?: string;
  gstin?: string;
  cin?: string;
  sacCode?: string;
}

export interface CreateCentreDto {
  mainCentreId: string;
  name: string;
  code: string;
}

export interface CreateMainCentreDto {
  /** The city, shown in the top bar and on the login screen. */
  name: string;
  code: string;
  /** Receipt header — shared by every centre under this main centre. */
  instituteName: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  phoneNumber: string;
  email: string;
  gstin: string;
  cin?: string;
  sacCode?: string;
}

export type UpdateMainCentreDto = Partial<CreateMainCentreDto>;

export const masterService = {
  list: (resource: MasterResource) =>
    apiClient.get<{ items: MasterItem[] }>(`/${resource}`, { params: { limit: 100 } }),

  /** Centres of one main centre — the top bar and login screen list one city at a time. */
  listCentres: (mainCentreId?: string) =>
    apiClient.get<{ items: CentreItem[] }>('/centres', {
      params: { limit: 100, ...(mainCentreId ? { mainCentreId } : {}) },
    }),

  listMainCentres: () =>
    apiClient.get<{ items: MainCentreItem[] }>('/main-centres', { params: { limit: 100 } }),

  create: (resource: MasterResource, name: string) =>
    apiClient.post<{ item: MasterItem }>(`/${resource}`, { name }),

  createCentre: (body: CreateCentreDto) =>
    apiClient.post<{ item: CentreItem }>('/centres', { ...body, active: true }),

  createMainCentre: (body: CreateMainCentreDto) =>
    apiClient.post<{ item: MainCentreItem }>('/main-centres', { ...body, active: true }),

  updateMainCentre: (id: string, body: UpdateMainCentreDto) =>
    apiClient.patch<{ item: MainCentreItem }>(`/main-centres/${id}`, body),

  remove: (resource: MasterResource, id: string) =>
    apiClient.delete<{ item: MasterItem }>(`/${resource}/${id}`),
};
